import { interval as observableInterval } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { Commit, Dispatch } from 'vuex';
import StoreUtil from '@app/store/StoreUtil';
import History, { CancelStatus } from '@models/user/history/History';
import { HistoryType } from '@store/modules/ui/user/history/enums';
import config from '@config';
import UserService from '@core/services/user/UserService';

export const APPEND_HISTORIES = 'APPEND_HISTORIES';
export const SET_HISTORIES = 'SET_HISTORIES';
export const SET_NEXT_HISTORIES = 'SET_NEXT_HISTORIES';
export const SET_IS_LOADING = 'SET_IS_LOADING';

export interface IHistoryState {
    histories: History[];
    nextHistories: History[];
    isLoading: boolean;
}

function defaultState(): IHistoryState {
    return {
        histories: [],
        nextHistories: [],
        isLoading: false,
    };
}

export default {
    namespaced: true,

    state: defaultState(),

    mutations: {
        [APPEND_HISTORIES](state: IHistoryState, histories: History[]) {
            state.histories = state.histories.concat(histories);
        },
        [SET_HISTORIES]: StoreUtil.createSimpleMutator('histories'),
        [SET_NEXT_HISTORIES]: StoreUtil.createSimpleMutator('nextHistories'),
        [SET_IS_LOADING]: StoreUtil.createSimpleMutator('isLoading'),
    },

    getters: {
        histories: StoreUtil.createSimpleGetter('histories'),
        nextHistories: StoreUtil.createSimpleGetter('nextHistories'),
        isLoading: StoreUtil.createSimpleGetter('isLoading'),
    },

    actions: {
        fetchHistory: StoreUtil.asyncErrorGuard(
            async (
                { commit, dispatch }: { commit: Commit, dispatch: Dispatch },
                { type, page }: {type: HistoryType, page: number}
            ) => {
                dispatch('setIsLoading', true);
                const method: any = {
                    [HistoryType.bets]: 'fetchBets',
                    [HistoryType.casinoPlays]: 'fetchCasinoPlays',
                    [HistoryType.transactions]: 'fetchTransactions',
                };
                const [histories, nextHistories] = await Promise.all([
                    (UserService.getInstance() as any)[method[type]](page),
                    (UserService.getInstance() as any)[method[type]](page + 1),
                ]);
                commit(APPEND_HISTORIES, histories);
                commit(SET_NEXT_HISTORIES, nextHistories);
                dispatch('setIsLoading', false);
            }),
        cancelWithdrawal: StoreUtil.asyncErrorGuard(
            async (
                { dispatch, getters }: { dispatch: Dispatch, getters: any },
                history: History
            ) => {
                history.cancelStatus = CancelStatus.pending;
                try {
                    await UserService.getInstance().cancelWithdrawal(history);
                } catch (error) {
                    history.cancelStatus = CancelStatus.failure;
                    throw error;
                }
                const fetchTransactions = () => {
                    return UserService.getInstance().fetchTransactions(
                        findCancelWithdrawalPage(getters.histories, history));
                };
                await fetchTransactions();

                // Comtrade returns success on cancelWithdrawal but transaction
                // is changed after some period of time and immediate fetch returns
                // wrong data (this is by their design :( safe charge - com trade communication).
                // This observable fetches transaction until our transaction is cancelled
                const subscription = observableInterval(300).pipe(
                    timeout(10000))
                    .subscribe(
                        async () => {
                            const transaction = (await fetchTransactions())
                                .find((h: History) => h.id === history.id);
                            if (transaction && !transaction.isCancelable) {
                                subscription.unsubscribe();
                                const storeTransaction = getSelectedHistory(getters, history);
                                storeTransaction.cancelStatus = CancelStatus.success;
                                storeTransaction.isCancelable = false;
                                storeTransaction.status = transaction.status;
                                storeTransaction.title = transaction.title;
                            }
                        },
                        (err: any) => {
                            const storeTransaction = getSelectedHistory(getters, history);
                            storeTransaction.cancelStatus = CancelStatus.failure;
                            throw err;
                        },
                    );
            }),
        reset({ commit }: { commit: Commit }) {
            commit(SET_HISTORIES, []);
            commit(SET_NEXT_HISTORIES, []);
            commit(SET_IS_LOADING, false);
        },
        setIsLoading: StoreUtil.createSimpleMutatorAction(SET_IS_LOADING),
    },
};

function findCancelWithdrawalPage(histories: History[], history: History) {
    const index = histories.findIndex((h: History) => h.id === history.id);
    return Math.floor(index / config.app.user.history.pageSize + 1);
}

const getSelectedHistory = (getters: any, history: History) =>
    getters.histories.find((h: History) => h.id === history.id);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--12!./src/app/store/modules/ui/user/history/historyIndex.ts